// src/pages/ApprovazioneFeriePermessiPage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { CalendarDays, CheckSquare, X } from "lucide-react";
import { useToast } from "../utils/Toast";
import { useCanSee } from "../hooks/useCanSee";
import { scriviGiornateSpeciali, scriviPermessoAOre } from "../utils/assenze";
import { descriviRichiesta } from "../utils/feriePermessi";

const STATI = {
  in_attesa: { label:"In attesa",  bg:"#dbeafe", col:"#1d4ed8" },
  approvata: { label:"Approvata",  bg:"#d1fae5", col:"#065f46" },
  rifiutata: { label:"Rifiutata",  bg:"#fee2e2", col:"#991b1b" },
};


// Tutti i giorni AAAA-MM-GG compresi tra due date, estremi inclusi.
function giorniCompresi(dataInizio, dataFine) {
  const out = [];
  const [y1,m1,g1] = dataInizio.split("-").map(Number);
  const [y2,m2,g2] = dataFine.split("-").map(Number);
  let cur = new Date(y1,m1-1,g1);
  const fine = new Date(y2,m2-1,g2);
  while (cur <= fine) {
    out.push({ anno:cur.getFullYear(), mese:cur.getMonth()+1, giorno:cur.getDate() });
    cur.setDate(cur.getDate()+1);
  }
  return out;
}

export default function ApprovazioneFeriePermessiPage() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const { addToast } = useToast();
  const canSee = useCanSee();
  const canManage = isOrgAdmin || isPlatformOwner || canSee("approvazione-ferie-permessi");

  const [richieste, setRichieste] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("in_attesa");
  const [elaborando, setElaborando] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"users"), where("organizationId","==",orgId), where("enabled","==",true)),
      snap => setUtenti(snap.docs.map(d => d.data())));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    return onSnapshot(query(collection(db,"richiesteFerie"), where("organizationId","==",orgId)), snap => {
      setRichieste(snap.docs.map(d => ({ id:d.id, ...d.data() })));
      setLoading(false);
    });
  }, [orgId]);

  const nomeUtente = (uid) => utenti.find(u => u.uid === uid)?.displayName || uid?.slice(0,8)+"…";

  // Scrive Ferie/Permesso direttamente nel cartellino dell'interessato, giorno per giorno —
  // stessa identica forma di dato che produce l'auto-timbratura di una "voce speciale" in
  // Presenze, così Report Ore e tutto il resto la trattano allo stesso modo. Se un mese è
  // già stato inviato/approvato (non più "bozza"), quel giorno NON viene toccato — un
  // cartellino chiuso non va mai modificato in automatico — e viene segnalato a chi approva
  // perché lo aggiunga a mano.
  // Stessa funzione di "Registra assenza" (utils/assenze.js): solo giorni feriali, nessun
  // giorno già compilato toccato, durata = ore standard dell'azienda. Prima si scrivevano anche
  // sabati, domeniche e festivi, la giornata contava 9 ore (08:00–17:00) e una seconda
  // approvazione duplicava le timbrature.
  const scriviInCartellino = async (uid, tipoRichiesta, dataInizio, dataFine, richiestaId) => {
    let oreGiornata = 8;
    try { const o = await getDoc(doc(db,"organizations",orgId)); oreGiornata = Number(o.data()?.oreLavorativeStandard) || 8; } catch { /* resta 8 */ }
    const { daGestireAMano } = await scriviGiornateSpeciali({
      orgId, uid, dal: dataInizio, al: dataFine, voce: tipoRichiesta === "ferie" ? "Ferie" : "Permesso",
      periodo: { soggetto: true, inizio: null, fine: null }, oreGiornata, marcatore: { richiestaFerieId: richiestaId || "" } });
    return daGestireAMano;
  };

  const aggiornaStato = async (r, nuovoStato) => {
    // Stesso principio di separazione dei compiti già usato per le Note Spese: nessuno
    // approva o respinge una propria richiesta, eccetto il Platform Owner per le sue prove.
    const staBypassando = r.uid === currentUser?.uid;
    if (staBypassando && !isPlatformOwner) {
      addToast("Non puoi approvare o respingere una tua stessa richiesta — serve un altro amministratore","error");
      return;
    }
    setElaborando(r.id);
    try {
      let daGestireAMano = [];
      if (nuovoStato === "approvata") {
        if (r.aOre) {
          // Permesso a ore: si aggiunge a quella giornata, anche se la persona lavora
          const esito = await scriviPermessoAOre({ orgId, uid: r.uid, giorno: r.dataInizio, dalle: r.oraInizio, alle: r.oraFine, marcatore: { richiestaFerieId: r.id } });
          daGestireAMano = [...esito.daGestireAMano, ...esito.saltati.map(x => `${x.data.split("-").reverse().join("/")} (${x.motivo})`)];
        } else {
          daGestireAMano = await scriviInCartellino(r.uid, r.tipo, r.dataInizio, r.dataFine, r.id);
        }
      }
      await updateDoc(doc(db,"richiesteFerie",r.id), {
        stato: nuovoStato,
        [`${nuovoStato}Da`]: currentUser.uid,
        [`${nuovoStato}At`]: serverTimestamp(),
        ...(daGestireAMano.length ? { giorniDaGestireAMano: daGestireAMano } : {}),
        ...(staBypassando ? { [`${nuovoStato}ComeBypassPlatformOwner`]: true } : {}),
      });
      if (nuovoStato === "approvata") {
        addToast(daGestireAMano.length
          ? `Approvata ✅ — attenzione: ${daGestireAMano.join(", ")}: non scritto nel cartellino (già chiuso o occupato), da sistemare a mano`
          : "Approvata ✅ — aggiunta automaticamente al cartellino", daGestireAMano.length?"info":"success");
      } else {
        addToast("Richiesta respinta","info");
      }
    } catch(e) {
      addToast(messaggioErrore(e), "error");
    }
    setElaborando(null);
  };

  const richiesteFiltrate = richieste
    .filter(r => filtroStato==="tutti" ? true : r.stato===filtroStato)
    .sort((a,b) => (b.createdAt?.toMillis?.()||0) - (a.createdAt?.toMillis?.()||0));

  const count = {
    in_attesa: richieste.filter(r=>r.stato==="in_attesa").length,
    approvata: richieste.filter(r=>r.stato==="approvata").length,
    rifiutata: richieste.filter(r=>r.stato==="rifiutata").length,
  };

  if (!canManage) {
    return <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>Non hai i permessi per vedere questa pagina.</div>;
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ background:"linear-gradient(135deg,#1e1b4b,#312e81)", padding:"16px 24px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
          <CalendarDays size={20} color="#c4b5fd"/>
          <h2 style={{ fontSize:18, fontWeight:800, color:"#fff", margin:0 }}>Approvazione Ferie e Permessi</h2>
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {[
            { id:"in_attesa", label:`In attesa (${count.in_attesa})`, bg:"#dbeafe", col:"#1d4ed8" },
            { id:"approvata", label:`Approvate (${count.approvata})`, bg:"#d1fae5", col:"#065f46" },
            { id:"rifiutata", label:`Rifiutate (${count.rifiutata})`, bg:"#fee2e2", col:"#991b1b" },
            { id:"tutti",     label:"Tutte", bg:"#f3f4f6", col:"#374151" },
          ].map(f => (
            <button key={f.id} onClick={() => setFiltroStato(f.id)}
              style={{ padding:"5px 12px", borderRadius:20, border:"none",
                background: filtroStato===f.id ? f.bg : "rgba(255,255,255,0.1)",
                color: filtroStato===f.id ? f.col : "rgba(255,255,255,0.6)",
                fontSize:11, fontWeight: filtroStato===f.id?700:400,
                cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:24, background:"#f9fafb" }}>
        {loading && <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>⏳ Caricamento...</div>}
        {!loading && richiesteFiltrate.length===0 && (
          <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
            <CalendarDays size={40} color="#d1d5db" style={{ marginBottom:12 }}/>
            <div style={{ fontSize:14 }}>{filtroStato==="in_attesa" ? "Nessuna richiesta in attesa" : "Nessuna richiesta"}</div>
          </div>
        )}
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {richiesteFiltrate.map(r => {
            const nome = nomeUtente(r.uid);
            const s = STATI[r.stato] || STATI.in_attesa;
            const suaStessa = r.uid === currentUser?.uid;
            return (
              <div key={r.id} style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e7eb",
                padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:40, height:40, borderRadius:"50%", background:"#ede9fe",
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:"#7c3aed", flexShrink:0 }}>
                    {nome[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{nome}</div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>
                      {descriviRichiesta(r)}
                    </div>
                    {r.note && <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{r.note}</div>}
                    {r.stato==="approvata" && r.giorniDaGestireAMano?.length > 0 && (
                      <div style={{ fontSize:11, color:"#dc2626", marginTop:4, fontWeight:600 }}>
                        ⚠️ Da sistemare a mano nel cartellino (già chiuso o occupato): {r.giorniDaGestireAMano.join(", ")}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <span style={{ fontSize:11, fontWeight:700, color:s.col, background:s.bg, borderRadius:20, padding:"3px 10px" }}>
                    {s.label}
                  </span>
                  {r.stato==="in_attesa" && suaStessa && !isPlatformOwner && (
                    <div style={{fontSize:11.5,color:"#9ca3af",fontStyle:"italic",padding:"6px 4px"}}>
                      È una tua richiesta — serve un altro amministratore
                    </div>
                  )}
                  {r.stato==="in_attesa" && (!suaStessa || isPlatformOwner) && (
                    <>
                      {suaStessa && isPlatformOwner && (
                        <span style={{fontSize:10,color:"#7c3aed",fontWeight:700,marginRight:4}} title="È una tua richiesta — puoi agire solo perché sei Platform Owner">⚠️ tua richiesta</span>
                      )}
                      <button disabled={elaborando===r.id} onClick={() => aggiornaStato(r,"approvata")}
                        style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                          border:"1px solid #059669", borderRadius:7, background:"#d1fae5", color:"#059669",
                          fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif", opacity:elaborando===r.id?0.6:1 }}>
                        <CheckSquare size={13}/> Approva
                      </button>
                      <button disabled={elaborando===r.id} onClick={() => aggiornaStato(r,"rifiutata")}
                        style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 14px",
                          border:"1px solid #dc2626", borderRadius:7, background:"#fee2e2", color:"#dc2626",
                          fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif", opacity:elaborando===r.id?0.6:1 }}>
                        <X size={13}/> Respingi
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
